// components/Navbar.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Container,
  useMediaQuery,
  styled
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FaStar, FaBars } from 'react-icons/fa';

// Custom styled components
const NavLink = styled(Link)(({ theme, isactive }) => ({
  color: 'white',
  textDecoration: 'none',
  fontSize: '16px',
  fontWeight: 500,
  letterSpacing: '0.5px',
  padding: '8px 16px',
  margin: '0 4px',
  position: 'relative',
  whiteSpace: 'nowrap',
  transition: 'all 0.3s',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    width: isactive === 'true' ? '80%' : '0%',
    height: '3px',
    background: theme.palette.secondary.main,
    transition: 'all 0.3s ease',
    transform: 'translateX(-50%)',
    borderRadius: '3px',
    opacity: isactive === 'true' ? 1 : 0,
  },
  
  '&:hover': {
    color: theme.palette.secondary.main,
    transform: 'translateY(-2px)',
    
    '&::after': {
      width: '80%',
      opacity: 1,
    }
  },
}));

// Custom ticket-shaped button
const TicketButton = styled(Link)(({ theme }) => ({
  display: 'inline-block',
  padding: '10px 24px',
  background: theme.palette.secondary.main,
  color: '#0a1929',
  fontWeight: 'bold',
  textDecoration: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  letterSpacing: '0.5px',
  transition: 'all 0.3s',
  position: 'relative',
  textAlign: 'center',
  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
  
  // Ticket notches
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    width: '12px', 
    height: '12px',
    borderRadius: '50%',
    background: 'rgba(10, 25, 41, 0.8)',
    transform: 'translateY(-50%)',
  },
  
  '&::before': {
    left: '-6px',
  },
  
  '&::after': {
    right: '-6px',
  },
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
    background: theme.palette.secondary.light,
  }
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none', 
  background: 'rgba(10, 25, 41, 0.85)', 
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(255,255,255,0.05)',
}));

// Animated logo
const LogoLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: 'white',
  textDecoration: 'none',
  transition: 'transform 0.3s',
  '&:hover': {
    transform: 'scale(1.05)',
    '& .logo-star': {
      transform: 'rotate(180deg)',
      color: theme.palette.secondary.main,
    },
  },
}));

// Make the star icon rotate on hover
const LogoStar = styled(FaStar)(({ theme }) => ({
  transition: 'transform 0.6s, color 0.3s',
  color: theme.palette.secondary.main,
}));

const navLinks = [
  { title: 'Now Playing', path: '/now-playing' },
  { title: 'Coming Soon', path: '/coming-soon' },
  { title: 'Dining', path: '/dining' },
  { title: 'Membership', path: '/membership' },
  { title: 'Events', path: '/events' },
  { title: 'About', path: '/about' }
];

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ 
      textAlign: 'center', 
      height: '100%',
      background: 'linear-gradient(135deg, #0a1929 0%, #152238 100%)',
      py: 2,
    }}>
      <LogoLink to="/" sx={{ justifyContent: 'center', mb: 4 }}>
        <LogoStar className="logo-star" size={24} />
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '1px' }}>
          Stellar Cinemas
        </Typography>
      </LogoLink>
      
      <List>
        {navLinks.map((link) => (
          <ListItem 
            key={link.title} 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              transition: 'all 0.3s',
              backgroundColor: location.pathname === link.path ? 'rgba(255,255,255,0.05)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              }
            }}
          >
            <NavLink 
              to={link.path} 
              isactive={(location.pathname === link.path).toString()}
              sx={{ py: 1, width: '100%', textAlign: 'center' }}
            >
              {link.title}
            </NavLink>
          </ListItem>
        ))}
        
        <ListItem sx={{ justifyContent: 'center', mt: 2 }}>
          <TicketButton to="/tickets">
            Buy Tickets
          </TicketButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <StyledAppBar 
      position="sticky" 
      sx={{ 
        py: scrolled ? 0.5 : 1.5, 
        transition: 'padding 0.3s'
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          {/* Logo */}
          <LogoLink to="/">
            <LogoStar className="logo-star" size={28} />
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                letterSpacing: '.2rem',
              }}
            >
              Stellar Cinemas
            </Typography>
          </LogoLink>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              height: '40px' 
            }}>
              {navLinks.map((link) => (
                <NavLink 
                  key={link.title} 
                  to={link.path}
                  isactive={(location.pathname === link.path).toString()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%'
                  }}
                >
                  {link.title}
                </NavLink>
              ))}
              
              <Box sx={{ ml: 2 }}>
                <TicketButton to="/tickets">
                  Buy Tickets
                </TicketButton>
              </Box>
            </Box>
          )}

          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ 
                ml: 2,
                backgroundColor: 'rgba(255,255,255,0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                }
              }}
            >
              <FaBars />
            </IconButton>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        PaperProps={{
          sx: {
            width: 280,
            backgroundColor: 'background.default',
          }
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
        }}
      >
        {drawer}
      </Drawer>
    </StyledAppBar>
  );
};

export default Navbar;